import type {
  BuyMembershipHostData,
  Membership,
  MembershipBranch,
  MembershipUser,
} from '../components/booking-purchase-dialog/buyMembership.types';
import { mapPriceBreakdownResponse } from './mapPriceBreakdownResponse';

type Scope = {
  $on: (event: '$destroy', callback: () => void) => void;
};

type Modal = {
  element: { modal: () => void };
  close: Promise<any>;
};

type BuyMembershipHostDependencies = {
  getCurrentBranch: () => MembershipBranch;
  getCurrentUser: () => MembershipUser;
  isFeatureFlagEnabled: (name: string) => boolean;
  showModal: (options: any) => Promise<Modal>;
  hideAllModals: () => void;
  openTerms: (options: any) => void;
  formatMoney: (amount: number, currency: string) => string;
  formatDuration: (count?: number, unit?: string) => string;
  todayLocal: (timezone?: string) => string;
  addLocalDays: (date: string, days: number, timezone?: string) => string;
  formatLocalDate: (date: string, timezone?: string) => string;
  toUtcDate: (date: string, timezone?: string) => string;
  canBeBoughtByMember: (...args: any[]) => Promise<any>;
  getPriceBreakdown: (request: any[]) => Promise<any>;
  buildPriceBreakdown: (breakdown: any, options: any) => any;
  buyPlan: (input: any) => Promise<any>;
  createCart: (
    products: any[],
    promoCode: string | undefined,
    autoRenewalTermsAccepted: boolean
  ) => Promise<{ cartId?: string; message?: string }>;
  triggerAnalyticsEvent: (payload: any) => void;
  reportAnalytics: (payload: any) => void;
  postMessageToParent: (payload: any, target: string) => void;
  sendUserInteraction: (event: string) => void;
  getStoredUserInteraction: () => any;
  setStoredUserInteraction: (value: any) => void;
  getCurrentPath: () => string;
  getAbsUrl: () => string;
  copyToClipboard: (value: string) => void;
  onRootEvent: (event: string, callback: () => void) => () => void;
  goToClassesWeekView: (branchId?: string) => void;
  scrollIframeToTop: () => void;
  clone: <T>(value: T) => T;
  now: () => number;
};

type MembershipSession = {
  branch: MembershipBranch;
  user: MembershipUser;
  data: BuyMembershipHostData | null;
};

function isCreditPack(plan: any) {
  return plan?.type === 'num_classes';
}

function isSingleMembership(plan: any) {
  return !!plan && !plan.auto_renewal && !plan._subscription_plan_id;
}

function toPriceRequest(services: any[], promoCode?: string) {
  return services.map((service) => ({
    service_type: service.type,
    service_id: service.service_id,
    isCreditPack: service.isCreditPack,
    sub_service_id: service.sub_service_id,
    price: service.price,
    utc_start_date: service.utc_start_date,
    unit_price: service.price,
    quantity: 1,
    promo_code:
      promoCode && service.is_promo_code_applicable ? promoCode : undefined,
  }));
}

function flagEnabled(
  dependencies: BuyMembershipHostDependencies,
  name: string
) {
  return dependencies.isFeatureFlagEnabled(name);
}

function paymentElementEnabled(
  dependencies: BuyMembershipHostDependencies,
  plan: any
) {
  if (!plan) {
    return false;
  }
  const paymentElement = flagEnabled(
    dependencies,
    'is-membership-purchase-via-payment-element-allowed'
  );
  if (isCreditPack(plan)) {
    return (
      paymentElement &&
      flagEnabled(
        dependencies,
        'is-credit-pack-purchase-via-payment-element-allowed'
      )
    );
  }
  return (
    paymentElement &&
    (isSingleMembership(plan) ||
      flagEnabled(
        dependencies,
        'is-recurring-membership-purchase-via-payment-element-allowed'
      ))
  );
}

function membershipFlags(dependencies: BuyMembershipHostDependencies) {
  return {
    isFtcEnabled: flagEnabled(dependencies, 'is-click-to-cancel-enabled'),
    membershipPromoEnabled: flagEnabled(
      dependencies,
      'promo-codes-web-portal-memberships'
    ),
    creditPackPromoEnabled: flagEnabled(
      dependencies,
      'promo-codes-web-portal-credit-packs'
    ),
    startDateEnabled: flagEnabled(
      dependencies,
      'webportal-membership-start-date'
    ),
    paymentElementEnabled: flagEnabled(
      dependencies,
      'is-membership-purchase-via-payment-element-allowed'
    ),
    recurringPaymentElementEnabled: flagEnabled(
      dependencies,
      'is-recurring-membership-purchase-via-payment-element-allowed'
    ),
    creditPackPaymentElementEnabled: flagEnabled(
      dependencies,
      'is-credit-pack-purchase-via-payment-element-allowed'
    ),
    joiningFeeDiscountEnabled: flagEnabled(
      dependencies,
      'is-joining-fee-discount-enabled'
    ),
  };
}

function showClosingModal(
  dependencies: BuyMembershipHostDependencies,
  options: any,
  onClose: (value: any) => any = () => undefined
) {
  return dependencies.showModal(options).then((modal) => {
    modal.element.modal();
    return modal.close.then(onClose);
  });
}

function showGeneralError(
  dependencies: BuyMembershipHostDependencies,
  message: string,
  title?: string
) {
  void dependencies
    .showModal({
      templateUrl: 'dialogs/general-dialog/general-dialog.html',
      controller: 'GeneralDialogController',
      controllerAs: 'vm',
      inputs: {
        data: {
          title: title || 'WEB_ERROR',
          message,
          buttonLabel: 'WEB_OK',
        },
      },
    })
    .then((modal) => modal.element.modal());
}

function termsContext(
  dependencies: BuyMembershipHostDependencies,
  membership: Membership,
  user: MembershipUser,
  extra: any = {}
) {
  const plan = membership.planToBuy;
  return {
    membershipName: membership.name,
    membershipPlan: plan.name,
    firstName: (user as any)?.first_name,
    lastName: (user as any)?.last_name,
    email: (user as any)?.email,
    frequency: dependencies.formatDuration(
      plan.duration_time_unit_count,
      plan.duration_time_unit
    ),
    joiningFee: plan.upfront_fee,
    isCreditPack: isCreditPack(plan),
    price: plan.price,
    ...extra,
  };
}

function getMembershipPriceBreakdown(
  ctx: {
    dependencies: BuyMembershipHostDependencies;
    session: MembershipSession;
    plan: Membership['planToBuy'];
  },
  services: any[],
  promoCode?: string
) {
  const { dependencies, session, plan } = ctx;
  return dependencies
    .getPriceBreakdown(toPriceRequest(services, promoCode))
    .then((quote) => {
      const breakdown = mapPriceBreakdownResponse(quote);
      const built = dependencies.buildPriceBreakdown(breakdown, {
        currency: session.branch?.address?.currency,
        services,
        subscriptionDetails: {
          isSubscription: !!plan.subscription_plan_id,
          fixedNumberOfCycles: !!plan.subscription_plan_duration,
          numCycles: plan.subscription_plan_duration,
          autoRenewal: !!plan.auto_renewal,
        },
        isRecurringMembership:
          !isSingleMembership(plan) && !isCreditPack(plan),
      });
      return {
        rows: built.rows,
        footnotes: built.footnotes,
        totalPrice: breakdown.total_price,
        isProRated: breakdown.isProRated,
        appliedDiscounts: breakdown.applied_discounts,
        appliedFutureDiscounts: breakdown.applied_discounts_future,
      };
    });
}

function createMembershipCart(
  dependencies: BuyMembershipHostDependencies,
  input: {
    membershipId: string;
    planCode: string;
    utcStartDate?: string;
    promoCode?: string;
    autoRenewalTermsAccepted: boolean;
  }
) {
  return dependencies.createCart(
    [
      {
        service_type: 'memberships',
        service_id: input.membershipId,
        sub_service_id: input.planCode,
        startDate: input.utcStartDate,
      },
    ],
    input.promoCode,
    input.autoRenewalTermsAccepted
  );
}

function syncUser(
  session: MembershipSession,
  dependencies: BuyMembershipHostDependencies
) {
  session.user = dependencies.getCurrentUser();
  session.branch = dependencies.getCurrentBranch();
  if (!session.data) {
    return;
  }
  session.data.user = dependencies.clone(session.user);
  session.data.branch = session.branch;
}

function showLoginRegister(
  session: MembershipSession,
  dependencies: BuyMembershipHostDependencies
) {
  if (session.data?.flags.isFtcEnabled && !session.data.isCustomLink) {
    dependencies.hideAllModals();
  }
  return showClosingModal(
    dependencies,
    {
      templateUrl: 'dialogs/login-register/login-register.html',
      controller: 'LoginRegisterController',
      controllerAs: 'vm',
      inputs: { isGoingToBook: true, showRegisterForm: false },
    },
    () => syncUser(session, dependencies)
  );
}

function abandonmentStillFresh(userId: string, interaction: any, now: number) {
  const expired = now - (interaction.date || 0) > 2 * 60 * 60 * 1000;
  return interaction.userId === userId && !expired;
}

function recordCartAbandonment(
  dependencies: BuyMembershipHostDependencies,
  userId: string
) {
  if (!flagEnabled(dependencies, 'Web-Cart-abandonment')) {
    return;
  }
  const interaction = dependencies.getStoredUserInteraction() || {};
  if (abandonmentStillFresh(userId, interaction, dependencies.now())) {
    return;
  }
  dependencies.setStoredUserInteraction({
    userId,
    date: dependencies.now(),
  });
  dependencies.sendUserInteraction('MEMBER_SERVICE_INTERACTION');
}

function trackPurchaseIntent(
  session: MembershipSession,
  dependencies: BuyMembershipHostDependencies,
  membership: Membership
) {
  const userId = session.user?._id;
  if (!userId) {
    return;
  }
  dependencies.reportAnalytics({
    event: 'MEMBERSHIP_PURCHASE_INTENT',
    branchId: session.branch?._id,
    membership,
    membership_id: membership._id,
    plan: membership.planToBuy,
    price: membership.planToBuy.price,
  });
  recordCartAbandonment(dependencies, userId);
}

function analyticsPayload(
  session: MembershipSession,
  membership: Membership,
  details: { event: string; price: number; selectedStartDate?: string }
) {
  return {
    event: details.event,
    branchId: session.branch?._id,
    membership,
    membership_id: membership._id,
    plan: membership.planToBuy,
    price: details.price,
    selectedStartDate: details.selectedStartDate,
  };
}

function copyShareUrl(
  dependencies: BuyMembershipHostDependencies,
  session: MembershipSession,
  membership: Membership
) {
  const baseUrl = dependencies.getAbsUrl().split('#')[0];
  dependencies.copyToClipboard(
    `${baseUrl}#/branch/${session.branch?._id}/memberships/${membership._id}/plan/${membership.planToBuy.code}/buy`
  );
  dependencies.triggerAnalyticsEvent({
    category: 'buy-membership',
    name: 'LINK_COPIED_TO_CLIPBOARD',
  });
}

function buildHostData(
  session: MembershipSession,
  dependencies: BuyMembershipHostDependencies,
  args: {
    membership: Membership;
    membershipsTrial: Array<{ _id?: string }>;
    dismissModal: () => void;
  }
): BuyMembershipHostData {
  const { membership, membershipsTrial, dismissModal } = args;
  const plan = membership.planToBuy;
  const timezone = session.branch?.address?.timezone_id;
  return {
    membership,
    membershipsTrial: membershipsTrial || [],
    user: dependencies.clone(session.user),
    branch: session.branch,
    currency: session.branch?.address?.currency || 'EUR',
    flags: membershipFlags(dependencies),
    isCustomLink: dependencies.getCurrentPath().includes('/buy'),
    formatMoney: dependencies.formatMoney,
    formatDuration: dependencies.formatDuration,
    todayLocal: () => dependencies.todayLocal(timezone),
    addLocalDays: (date, days) =>
      dependencies.addLocalDays(date, days, timezone),
    formatLocalDate: (date) =>
      dependencies.formatLocalDate(date, timezone),
    toUtcDate: (date) => dependencies.toUtcDate(date, timezone),
    canBeBoughtByMember: dependencies.canBeBoughtByMember,
    getPriceBreakdown: (services, promoCode) =>
      getMembershipPriceBreakdown(
        { dependencies, session, plan },
        services,
        promoCode
      ),
    buyPlan: dependencies.buyPlan,
    createCart: (input) => createMembershipCart(dependencies, input),
    showPaymentCollector: (paymentMethod, cartId) =>
      showClosingModal(
        dependencies,
        {
          templateUrl: 'dialogs/payment-collector/payment-collector.html',
          controller: 'PaymentCollectorController',
          controllerAs: 'vm',
          inputs: {
            paymentMethod,
            cartId: cartId || '',
            origin: 'buy-membership',
            hasNewPaymentMethodFeature: paymentElementEnabled(
              dependencies,
              plan
            ),
          },
        },
        (result) => {
          syncUser(session, dependencies);
          return result;
        }
      ),
    showLoginRegister: () => showLoginRegister(session, dependencies),
    showPurchaseTerms: (continuePurchase) => {
      const extra = continuePurchase
        ? { notDismissable: true, buttonAction: continuePurchase }
        : {};
      dependencies.openTerms({
        mode: 'membership',
        context: termsContext(dependencies, membership, session.user, extra),
      });
    },
    showAutorenewalTerms: () =>
      dependencies.openTerms({
        mode: 'membership',
        context: termsContext(dependencies, membership, session.user),
      }),
    showGeneralError: (message, title) =>
      showGeneralError(dependencies, message, title),
    trackIntent: () =>
      trackPurchaseIntent(session, dependencies, membership),
    trackEvent: dependencies.triggerAnalyticsEvent,
    trackPurchaseSuccess: (price, selectedStartDate) => {
      const payload = analyticsPayload(session, membership, {
        event: 'MEMBERSHIP_PURCHASE_SUCCESS',
        price,
        selectedStartDate,
      });
      dependencies.reportAnalytics(payload);
      dependencies.postMessageToParent(
        {
          messageType: 'GLOFOX_MEMBERSHIP_PURCHASE_SUCCESS',
          branchId: session.branch?._id,
          membership,
          membership_id: membership._id,
          plan,
          price,
          selectedStartDate,
        },
        '*'
      );
    },
    trackPurchaseFailure: (response, selectedStartDate) =>
      dependencies.reportAnalytics({
        ...analyticsPayload(session, membership, {
          event: 'MEMBERSHIP_PURCHASE_ERROR',
          price: plan.price!,
          selectedStartDate,
        }),
        response,
      }),
    copyShareUrl: () => copyShareUrl(dependencies, session, membership),
    scrollIframeToTop: dependencies.scrollIframeToTop,
    onClose: dismissModal,
    onDone: () => {
      dismissModal();
      dependencies.goToClassesWeekView(session.branch?._id);
    },
  };
}

function subscribeHost(
  scope: Scope,
  session: MembershipSession,
  dependencies: BuyMembershipHostDependencies
) {
  const unbindUserChange = dependencies.onRootEvent('userChange', () =>
    syncUser(session, dependencies)
  );
  const unbindPaymentMethodsSet = dependencies.onRootEvent(
    'paymentMethodsSet',
    () => syncUser(session, dependencies)
  );
  scope.$on('$destroy', () => {
    unbindUserChange();
    unbindPaymentMethodsSet();
  });
}

export function createBuyMembershipHost(
  dependencies: BuyMembershipHostDependencies
) {
  function create(
    scope: Scope,
    membership: Membership,
    membershipsTrial: Array<{ _id?: string }>,
    dismissModal: () => void
  ): BuyMembershipHostData {
    const session: MembershipSession = {
      branch: dependencies.getCurrentBranch(),
      user: dependencies.getCurrentUser(),
      data: null,
    };
    session.data = buildHostData(session, dependencies, {
      membership,
      membershipsTrial,
      dismissModal,
    });
    subscribeHost(scope, session, dependencies);
    return session.data;
  }

  return { create };
}
